// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useIsFetching } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect, useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { CommandPalette } from "@/components/layout/command-palette";
import { Header } from "@/components/layout/header";
import { MainScrollArea } from "@/components/layout/main-scroll-area";
import { PageFooterProvider, PageFooterSlot } from "@/components/layout/page-footer";
import { PageTitleProvider } from "@/components/layout/page-title-context";
import { AppSidebar } from "@/components/layout/sidebar";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { NoAccess } from "@/components/shared/no-access";
import { PageProgressBar } from "@/components/shared/page-loading";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { AssistantRoot } from "@/features/assistant/assistant-root";
import { SecondFactorNoticeDialog } from "@/features/profile/second-factor-notice-dialog";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS, userHasPermission } from "@/lib/auth/page-permissions";
import { DEFAULT_AUTHENTICATED_PATH } from "@/lib/routes";

/**
 * Build the `/login` search for a failed auth check. We carry the full target
 * `href` (so query params like filters are restored after login), except when
 * the target would resolve to the default landing page anyway — the bare root,
 * the login page itself, or the default page with no query. In those cases the
 * `?redirect` is just noise (login falls back to the default page), so we omit it.
 *
 * The SSO `state` param is always carried through: it's a one-time claim token
 * that login must consume, so it must survive the auth redirect regardless of
 * which path the callback happened to land on.
 */
function loginRedirectSearch(location: { pathname: string; href: string; search: Record<string, unknown> }): {
  redirect?: string;
  state?: string;
} {
  const state = typeof location.search.state === "string" ? location.search.state : undefined;
  const carry = state ? { state } : {};
  if (location.pathname === "/" || location.pathname === "/login") return carry;
  if (location.href === DEFAULT_AUTHENTICATED_PATH) return carry;
  return { redirect: location.href, ...carry };
}

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: ({ location }) => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      throw redirect({ to: "/login", search: loginRedirectSearch(location) });
    }
  },
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { isLoading, isAuthenticated, user } = useAuth();
  const isFetching = useIsFetching();
  const location = useLocation();
  const navigate = useNavigate();

  // Depend on the stable href/pathname primitives, not the `location` object — useLocation() returns a
  // fresh reference on every router update, which would re-fire this effect → navigate → loop.
  // biome-ignore lint/correctness/useExhaustiveDependencies: location.href/pathname are the stable trigger; the full location object would loop
  useEffect(
    function redirectToLoginWhenUnauthenticated() {
      if (!isLoading && !isAuthenticated) {
        navigate({ to: "/login", search: loginRedirectSearch(location), replace: true });
      }
    },
    [isLoading, isAuthenticated, navigate, location.href, location.pathname],
  );

  // The session bootstrap fetches the back-office context (permissions etc.), which gates the
  // whole app. Show a centered indeterminate spinner while it loads instead of a blank page.
  if (isLoading) {
    return (
      <div className="flex h-svh items-center justify-center bg-sidebar">
        <InlineSpinner />
      </div>
    );
  }
  if (!isAuthenticated) return null;

  const hasAnyBackofficeAccess = Object.values(PAGE_PERMISSIONS).some((perm) => userHasPermission(user, perm));
  if (!hasAnyBackofficeAccess) return <NoAccess />;

  return (
    <PageTitleProvider>
      <PageFooterProvider>
        <AssistantRoot>
          <SidebarProvider className="h-svh !min-h-0">
            {isFetching > 0 && <PageProgressBar />}
            <AppSidebar />
            <CommandPalette />
            <SecondFactorNoticeDialog />
            <SidebarInset className="overflow-hidden">
              <Header />
              <MainScrollArea>
                <Outlet />
              </MainScrollArea>
              <PageFooterSlot />
            </SidebarInset>
          </SidebarProvider>
        </AssistantRoot>
      </PageFooterProvider>
    </PageTitleProvider>
  );
}
